/**
 * HelpPanel — referencia rápida del sistema. Se abre/cierra con tecla H.
 *
 * Es una referencia estática (no un walkthrough — eso lo hace TutorialManager).
 * Cubre los pilares que el alumno necesita saber: gestos, mouse, atajos,
 * exámenes, patrones, triage.
 */

const PANEL_ID = 'cgt-help-panel';
const STYLE_ID = 'cgt-help-styles';

interface Tab {
  id: string;
  label: string;
  html: string;
}

const TABS: Tab[] = [
  {
    id: 'modos',
    label: 'Modos',
    html: `
      <h4>Tres formas de trabajar</h4>
      <dl>
        <dt>🏥 Triage de sala <em>(recomendado)</em></dt>
        <dd>Sala de urgencias con 6 pacientes simultáneos. Clasificás por color (rojo/amarillo/verde/negro) antes que se deterioren. Scoring: +20 acierto, −5 over-triage, −30 sub-triage.</dd>
        <dt>🩺 Monitor Mode</dt>
        <dd>Box clínico estilo hospital: monitor multiparamétrico en vivo (ECG, SpO₂, pletismografía), acciones clínicas, drogas, voz del paciente, debrief IA al cerrar.</dd>
        <dt>🧠 Grafo 3D</dt>
        <dd>Modo estudio: explorá el caso como grafo (síntomas, hallazgos, exámenes, diagnósticos) con las manos frente a la cámara. Para aprender razonamiento.</dd>
      </dl>
      <p class="help-note">Al cerrar un modo volvés al selector. El último elegido queda como default.</p>
    `,
  },
  {
    id: 'monitor',
    label: 'Monitor',
    html: `
      <h4>Acciones clínicas en el box</h4>
      <p>Panel derecho: 11 acciones (auscultar, palpar, O₂, vía EV, ECG 12D, Rx, aspirina, interconsulta…). La IA genera respuesta realista con narrativa, cambio de vitales y teaching-note.</p>
      <h4 style="margin-top:14px">👂 Auscultación por zonas</h4>
      <p>Click en <b>👂 Auscultar</b> abre una silueta con 8 focos clickeables:</p>
      <ul>
        <li><b>Cardíacos:</b> aórtico, pulmonar, tricuspídeo, mitral</li>
        <li><b>Pulmonares:</b> vértices y bases, derechos e izquierdos</li>
      </ul>
      <p>El hallazgo se adapta al diagnóstico (IAM, EPOC, neumonía, IC…) y se lee con TTS.</p>
      <h4 style="margin-top:14px">🎤 Voz del paciente</h4>
      <p>Mantené presionado <b>🎤 Hablar</b> para preguntarle al paciente. Kimi responde en primera persona con emoción coherente (dolor, disnea, confusión) y voz ajustada a edad/sexo.</p>
      <h4 style="margin-top:14px">📋 Debrief al cerrar</h4>
      <p>Al clickear <b>← Volver</b> la IA analiza tu cronología completa y devuelve: resumen, fortalezas, omisiones críticas, pearls educativas, próximo caso sugerido y score 0-100. Exportable a Markdown.</p>
    `,
  },
  {
    id: 'gestos',
    label: 'Gestos',
    html: `
      <h4>Gestos de procedimiento (cámara + IA)</h4>
      <dl>
        <dt>🫸 RCP — Compresiones torácicas</dt>
        <dd>Dos manos <b>apiladas</b> frente a la cámara, movimiento vertical rítmico. El sistema cuenta compresiones y rate/min; ideal 100-120.</dd>
        <dt>⚡ Desfibrilación</dt>
        <dd>Dos palmas abiertas <b>separadas lateralmente</b>, empuje firme hacia la cámara. Un solo thrust = un shock.</dd>
        <dt>🫱 Pulso carotídeo</dt>
        <dd>Índice + medio extendidos (resto flexionados), sostené 1.5 s frente a la cámara.</dd>
      </dl>
      <p class="help-note">HUD azul arriba a la derecha del monitor muestra el progreso de cada gesto en tiempo real. Ajustá la sensibilidad con el selector del header si los gestos no disparan (alta = más fácil).</p>

      <h4 style="margin-top:14px">Gestos de grafo 3D</h4>
      <dl>
        <dt>👌 Pinch sobre nodo</dt><dd>Selecciona y expande.</dd>
        <dt>✊ Puño + arrastre</dt><dd>Mueve el nodo.</dd>
        <dt>✋ Palma abierta</dt><dd>Auscultación sobre el nodo bajo la mano.</dd>
        <dt>🙌 Dos manos pinch</dt><dd>Zoom de cámara.</dd>
        <dt>🙆 Dos manos arriba</dt><dd>Modo emergencia ABC.</dd>
      </dl>
    `,
  },
  {
    id: 'drogas',
    label: 'Drogas',
    html: `
      <h4>Farmacoterapia con curva real</h4>
      <p>Panel derecho del monitor: 12 drogas de urgencias. Cada administración desencadena curva farmacodinámica <b>rise → plateau → decay</b> que modifica vitales en tiempo real (no cambios instantáneos).</p>
      <ul>
        <li><b>Adrenalina 1 mg IV</b> — +30 HR, +40 SBP (onset 30 s, dur 3 min)</li>
        <li><b>Atropina 1 mg IV</b> — +25 HR (bradicardia)</li>
        <li><b>Amiodarona 300 mg IV</b> — −15 HR (FV/TV refractaria)</li>
        <li><b>Noradrenalina bolo</b> — +25 SBP (shock)</li>
        <li><b>O₂ 3 L cánula / 15 L reservorio</b> — +5 / +10 SpO₂</li>
        <li><b>Nitroglicerina SL</b> — −12 SBP (dolor torácico)</li>
        <li><b>Morfina 4 mg IV</b> — −4 RR (depresión resp.)</li>
        <li><b>Salbutamol NBZ</b> — +4 SpO₂, +10 HR (broncoespasmo)</li>
        <li><b>SF 500 ml bolo</b>, <b>Aspirina 300 mg VO</b>, <b>Furosemida 40 mg IV</b>, <b>NTG SL</b></li>
      </ul>
      <p class="help-note">Dosis repetidas de la misma droga tienen retorno decreciente (factor 0.6×, 0.36×…). Cap por vital evita valores absurdos.</p>
    `,
  },
  {
    id: 'profe',
    label: 'Profesor',
    html: `
      <h4>Dashboard (solo role=teacher)</h4>
      <p>Botón "Dashboard" en el header del alumno si tenés rol profesor. Cinco pestañas:</p>
      <dl>
        <dt>👥 Alumnos</dt>
        <dd>Lista con XP, casos, precisión, última actividad. Botón "+/✓" para asignar alumno a tu clase. En el detalle: sparkline de tendencia de score IA últimos 20 debriefs.</dd>
        <dt>📊 Analytics</dt>
        <dd>Métricas de cohorte, actividad diaria, distribución de precisión.</dd>
        <dt>📋 Asignaciones</dt>
        <dd>Creá tareas (caso + due date) y asignales a alumnos.</dd>
        <dt>🔐 Accesos</dt>
        <dd>Log de logins (éxito/fallo) para auditoría.</dd>
        <dt>🧠 Debriefs IA</dt>
        <dd>Todos los debriefs generados con score color-coded (verde ≥80, amarillo ≥60, rojo &lt;60), expandibles para ver strengths/gaps/pearls. Filtro "solo &lt;60".</dd>
        <dt>🔴 En vivo</dt>
        <dd>Cuando un alumno de tu clase abre Monitor, ves su feed: acciones, drogas, gestos detectados, turnos de voz, mini-monitor de vitales (actualizado c/ 8 s).</dd>
      </dl>
      <p class="help-note">Solo ves alumnos que te asignaron a su <code>teacher_id</code>. Asignalos desde la pestaña Alumnos.</p>
    `,
  },
  {
    id: 'patrones',
    label: 'Patrones',
    html: `
      <h4>Patrones detectados automáticamente</h4>
      <ul>
        <li><b>Beck</b>: hipotensión + ingurgitación yugular + ruidos apagados → tamponade</li>
        <li><b>Charcot</b>: dolor CSD + fiebre + ictericia → colangitis</li>
        <li><b>Cushing</b>: HTA + bradicardia + resp. irregular → HIC</li>
        <li><b>Meningitis</b>: fiebre + cefalea + rigidez de nuca</li>
        <li><b>qSOFA ≥ 2 / Sepsis-3</b></li>
        <li><b>STEMI / NSTEMI</b>: dolor torácico + troponina elevada</li>
        <li><b>DKA</b>: glicemia alta + acidosis + cetonemia</li>
        <li><b>EPOC exacerbado</b>, <b>TEP</b>, <b>SDRA</b></li>
      </ul>
      <h4 style="margin-top:14px">Escalas de triage</h4>
      <dl>
        <dt>START</dt><dd>Respiración + perfusión + estado mental.</dd>
        <dt>NEWS2</dt><dd>Score agregado FR, SpO₂, PAS, FC, conciencia, T°.</dd>
        <dt>qSOFA</dt><dd>FR ≥ 22, PAS ≤ 100, alteración conciencia.</dd>
        <dt>Killip</dt><dd>Severidad IC aguda en IAM (I–IV).</dd>
      </dl>
    `,
  },
  {
    id: 'atajos',
    label: 'Atajos',
    html: `
      <h4>Teclado (modo grafo 3D)</h4>
      <dl>
        <dt><kbd>1</kbd>–<kbd>4</kbd></dt><dd>Cambia topología (anatómico / fisiopatológico / temporal / diferencial).</dd>
        <dt><kbd>Espacio</kbd></dt><dd>Pausa / reanuda simulación.</dd>
        <dt><kbd>↑ ↓</kbd></dt><dd>Acelera / ralentiza tiempo clínico.</dd>
        <dt><kbd>R</kbd></dt><dd>Resetea reloj del paciente.</dd>
        <dt><kbd>C</kbd></dt><dd>Resetea cámara.</dd>
        <dt><kbd>L</kbd></dt><dd>Muestra / oculta labels.</dd>
        <dt><kbd>E</kbd></dt><dd>Muestra / oculta aristas.</dd>
        <dt><kbd>Esc</kbd></dt><dd>Modo emergencia ABC.</dd>
        <dt><kbd>H</kbd></dt><dd>Abre / cierra esta ayuda.</dd>
      </dl>
      <h4 style="margin-top:14px">Mouse (si no hay manos)</h4>
      <dl>
        <dt>Click</dt><dd>Selecciona + expande nodo.</dd>
        <dt>Doble click</dt><dd>Escala 2.5×.</dd>
        <dt>Arrastre</dt><dd>Mueve nodo.</dd>
        <dt>Rueda sobre nodo</dt><dd>Escala individual 0.5×–4×.</dd>
      </dl>
      <p class="help-note">La mano manda si está visible. El mouse es fallback.</p>
    `,
  },
];

export class HelpPanel {
  private root: HTMLElement | null = null;
  private activeTabId: string = TABS[0].id;

  mount(): void {
    this.injectStyles();
    window.addEventListener('keydown', this.onKeyDown);
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Ignorar si el usuario está escribiendo en un input/textarea
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

    if (e.key === 'h' || e.key === 'H') {
      e.preventDefault();
      this.toggle();
    } else if (e.key === 'Escape' && this.root) {
      this.close();
    }
  };

  toggle(): void {
    if (this.root) this.close();
    else this.open();
  }

  open(): void {
    if (this.root) return;
    this.root = this.buildDOM();
    document.body.appendChild(this.root);
    requestAnimationFrame(() => this.root?.classList.add('cgt-help-visible'));
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-help-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private buildDOM(): HTMLElement {
    const root = document.createElement('div');
    root.id = PANEL_ID;

    const tabButtons = TABS.map(t =>
      `<button type="button" class="cgt-help-tab${t.id === this.activeTabId ? ' cgt-help-tab-active' : ''}" data-tab="${t.id}">${t.label}</button>`
    ).join('');

    const initialContent = TABS.find(t => t.id === this.activeTabId)?.html ?? '';

    root.innerHTML = `
      <div class="cgt-help-backdrop" data-close></div>
      <div class="cgt-help-card" role="dialog" aria-label="Ayuda">
        <header>
          <div>
            <h2>Dr Newen <span class="cgt-help-ia">IA</span> · Ayuda</h2>
            <p class="cgt-help-sub">Referencia rápida del simulador.</p>
          </div>
          <button type="button" class="cgt-help-close" data-close aria-label="Cerrar">×</button>
        </header>
        <nav class="cgt-help-tabs">${tabButtons}</nav>
        <section class="cgt-help-content">${initialContent}</section>
        <footer>
          <kbd>H</kbd> abre/cierra · <kbd>Esc</kbd> cierra
        </footer>
      </div>
    `;

    root.querySelectorAll<HTMLElement>('[data-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );

    root.querySelectorAll<HTMLButtonElement>('.cgt-help-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.tab;
        if (!id) return;
        this.activeTabId = id;
        root.querySelectorAll('.cgt-help-tab').forEach(b => b.classList.remove('cgt-help-tab-active'));
        btn.classList.add('cgt-help-tab-active');
        const content = root.querySelector<HTMLElement>('.cgt-help-content');
        if (content) content.innerHTML = TABS.find(t => t.id === id)?.html ?? '';
      });
    });

    return root;
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; inset: 0; z-index: 15000;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        opacity: 0; transition: opacity 0.2s ease;
      }
      #${PANEL_ID}.cgt-help-visible { opacity: 1; }
      #${PANEL_ID} .cgt-help-backdrop {
        position: absolute; inset: 0; background: rgba(0,0,0,0.5);
        backdrop-filter: blur(4px);
      }
      #${PANEL_ID} .cgt-help-card {
        position: relative; max-width: 680px; width: 92vw;
        max-height: 80vh; margin: 10vh auto;
        background: #0a0a0a;
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 16px;
        display: flex; flex-direction: column; overflow: hidden;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
      }
      #${PANEL_ID} header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 18px 22px; border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} header h2 { font-size: 18px; font-weight: 700; letter-spacing: -0.3px; }
      #${PANEL_ID} header .cgt-help-sub { font-size: 11px; color: #6e6e73; margin-top: 2px; }
      #${PANEL_ID} header .cgt-help-ia {
        background: linear-gradient(90deg, #2997FF, #BF5AF2);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        background-clip: text;
      }
      #${PANEL_ID} .cgt-help-close {
        width: 28px; height: 28px; border-radius: 50%;
        background: rgba(255,255,255,0.06); border: none; cursor: pointer;
        color: #a1a1a6; font-size: 18px; line-height: 1;
      }
      #${PANEL_ID} .cgt-help-close:hover { background: rgba(255,255,255,0.12); color: #fff; }
      #${PANEL_ID} .cgt-help-tabs {
        display: flex; gap: 4px; padding: 12px 22px 0;
        border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} .cgt-help-tab {
        background: transparent; border: none;
        color: #a1a1a6; font-size: 12px; font-weight: 500;
        padding: 8px 12px; border-radius: 8px 8px 0 0;
        cursor: pointer;
      }
      #${PANEL_ID} .cgt-help-tab:hover { color: #fff; }
      #${PANEL_ID} .cgt-help-tab-active { color: #fff; background: rgba(255,255,255,0.06); }
      #${PANEL_ID} .cgt-help-content {
        padding: 20px 24px; overflow-y: auto; flex: 1;
        font-size: 13px; line-height: 1.55; color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-help-content h4 {
        font-size: 14px; font-weight: 700; color: #fff; margin-bottom: 12px;
      }
      #${PANEL_ID} .cgt-help-content dl { display: grid; grid-template-columns: max-content 1fr; gap: 6px 14px; }
      #${PANEL_ID} .cgt-help-content dt { font-weight: 600; color: #fff; white-space: nowrap; }
      #${PANEL_ID} .cgt-help-content dd { margin: 0; color: #a1a1a6; }
      #${PANEL_ID} .cgt-help-content ul { padding-left: 18px; margin-top: 4px; }
      #${PANEL_ID} .cgt-help-content li { margin-bottom: 6px; color: #a1a1a6; }
      #${PANEL_ID} .cgt-help-content li b { color: #fff; }
      #${PANEL_ID} .cgt-help-content kbd {
        display: inline-block; padding: 2px 7px; font-family: 'JetBrains Mono', monospace;
        font-size: 11px; background: #1a1a1a; border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 5px; color: #2997FF;
      }
      #${PANEL_ID} .help-note {
        margin-top: 10px; padding: 10px 14px;
        background: rgba(41,151,255,0.08); border-left: 2px solid #2997FF;
        border-radius: 6px; color: #a1a1a6; font-size: 12px;
      }
      #${PANEL_ID} footer {
        padding: 12px 22px; font-size: 11px; color: #6e6e73;
        border-top: 0.5px solid rgba(255,255,255,0.06); text-align: center;
      }
      #${PANEL_ID} footer kbd {
        display: inline-block; padding: 1px 5px; font-family: 'JetBrains Mono', monospace;
        font-size: 10px; background: #1a1a1a; border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 4px; color: #a1a1a6; margin: 0 2px;
      }
    `;
    document.head.appendChild(style);
  }
}

export const helpPanel = new HelpPanel();
